<script setup>
import { computed } from 'vue';
import merge from 'lodash/merge';
import { LineChart, ChartCard, Skeleton, MetricList } from '@dashhudson/dashing-ui';
import { NO_DATA_FOUND } from '@/app/socialListening/constants';
import { TIME_FORMATS, DEFAULT_TIME_FORMAT } from '@/components/foundation/charts/chartjs.defaults';
import { scaleLabelFormatter } from '@/utils/formatters';
import sum from 'lodash/sum';
import {
  formatChartDateTooltip,
  generateMetricTooltipItems,
} from '@/app/socialListening/utils/graph-utils';

const props = defineProps({
  /*
   * The configuration object for the ChartCard
   * Each key-value pair will become a prop for the ChartCard
   */
  chartCardProps: {
    type: Object,
    default: () => ({}),
  },
  /*
   * The datasets to display in the chart
   * @param {Array} data - A list of objects containing data points
   * @param {Number} data.x - The x coordinate, as a unix timestamp
   * @param {String} data.y - The y coordinate
   * @param {string} label - Should be one of POSITIVE, NEUTRAL, NEGATIVE
   *
   */
  datasets: {
    type: Array,
    default: null,
  },
  /*
   * Additional configuration for the chart
   */
  customChartConfig: {
    type: Object,
    default: () => ({}),
  },
  /*
   * The scale of the x axis
   * @param {String} scale - The scale of the x axis, should be one of 'day', 'month'
   */
  scale: {
    type: String,
    default: 'day',
  },
  /*
   * Whether to display default chart legend
   */
  showDefaultLegend: {
    type: Boolean,
    default: undefined,
  },
  /*
   * The chart legend options to display in the footer
   */
  chartLegendOptions: {
    type: Array,
    default: () => [],
  },
  /*
   * Whether the chart data is loading
   */
  loading: {
    type: Boolean,
    default: undefined,
  },
  /*
   * Whether to display only chart
   */
  chartOnly: {
    type: Boolean,
    default: undefined,
  },
  /*
   * Total sum of selected chart datasets
   */
  total: {
    type: Number,
    default: 0,
  },
  /*
   * Line chart properties
   */
  lineChartProps: {
    type: Object,
    default: () => {},
  },
  /*
   * Whether the chart is clickable
   */
  clickable: {
    type: Boolean,
    default: true,
  },
  /*
   * Function to generate metric items for tooltip
   */
  metricItemFunction: {
    type: Function,
    default: generateMetricTooltipItems,
  },
  tooltipTitleFunction: {
    type: Function,
    default: formatChartDateTooltip,
  },
});

const emit = defineEmits(['update:total']);

const wrapper = computed(() => (props.chartOnly ? 'div' : ChartCard));
const chartConfig = computed(() =>
  merge(
    {},
    {
      scales: {
        x: {
          type: 'time',
          time: {
            tooltipFormat: TIME_FORMATS[props.scale.toLowerCase()] || DEFAULT_TIME_FORMAT,
            unit: props.scale,
          },
        },
        y: {
          ticks: {
            callback: scaleLabelFormatter,
          },
          beginAtZero: true,
        },
      },
    },
    props.customChartConfig,
  ),
);
const chartData = computed(() => ({ datasets: props.datasets }));

const noChartData = computed(
  () => !chartData.value.datasets || chartData.value.datasets.length === 0,
);

function onVisibleDatasetsChange(visibleDatasets) {
  if (visibleDatasets.length) {
    const flatted = visibleDatasets.flatMap((dataset) => dataset.data).map((d) => d.y);
    emit('update:total', sum(flatted));
  } else {
    emit('update:total', 0);
  }
}
</script>

<template>
  <Skeleton v-if="loading" height="25rem" />
  <component :is="wrapper" v-else v-bind="chartCardProps">
    <LineChart
      v-bind="lineChartProps"
      :data="chartData"
      :options="chartConfig"
      height="240px"
      :show-legend="showDefaultLegend"
      :no-data="noChartData"
      :no-data-message="NO_DATA_FOUND"
      @visible-datasets="onVisibleDatasetsChange"
    >
      <template #tooltip="{ tooltip }">
        <span class="dash-typo-base-semibold">
          {{ props.tooltipTitleFunction(tooltip) }}
        </span>
        <MetricList
          :metric-items="props.metricItemFunction(tooltip, lineChartProps?.formatConfig)"
        />
        <span v-if="clickable" class="dash-text-subtle dash-text-base">
          Click a node to view posts
        </span>
      </template>
    </LineChart>
    <template v-if="!showDefaultLegend">
      <slot name="legend"></slot>
    </template>
    <template #filters>
      <slot name="filters"></slot>
    </template>
  </component>
</template>
